import { createContext, useContext, useSyncExternalStore } from 'react';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import type { QueryClient } from '@tanstack/react-query';
import { pendingWrites, queryClient } from './query';
import { browserLocks, idbKv, safeLocalStorage, type Kv } from './browser';
import {
  CacheAccount,
  accountFromSession,
  accountKey,
  isSessionQueryKey,
  shouldDehydrateQuery,
  type CacheAccountSnapshot,
  type MoveLegacyWrites,
  type MarkerStorage,
  type SessionAnswer,
} from './cache-account';
import {
  IdentityTabs,
  browserRoutes,
  useTabsSnapshot,
  type TabRoutes,
  type TabsSnapshot,
  type Timers,
} from './identity-tabs';
import { migrateLegacyDraftsOnThisDevice } from './drafts';
import { uploadQueue } from './upload-queue';
import { shouldDehydrateMutation } from './offline-replay';
import { moveLegacyWrites } from './legacy-writes';
import { indexedDbPendingWriteStore, type WriteHandOff } from './pending-writes';
import { failedWriteLog, indexedDbFailedWriteStore } from './failed-writes';
import type { AccountRef } from './account';

/**
 * MAP 225a, wired: the saved cache's account and the channel to other tabs, built together
 * because each tells the other something. A confirmed change of account is announced to the
 * other tabs; another tab's change stops this one and makes it ask the server again.
 *
 * `wireIdentity` is what the app runs once, below, and what a test runs for itself.
 */

/**
 * What is saved. Queries: TanStack's default minus `me.session`, which is never saved.
 * Mutations: none. Queued writes have their own account-keyed store (MAP 225b).
 */
export const dehydrateOptions = { shouldDehydrateMutation, shouldDehydrateQuery };

/** The slice of the queued writes the identity code drives (pending-writes.ts). */
export interface IdentityWrites {
  /**
   * The account this tab writes as: the account the gate draws, or nobody; `confirmedAt` is
   * the network answer's time, or null for the saved account drawn offline.
   */
  setAccount(account: AccountRef | null, confirmedAt?: number | null): void;
  /** Nothing is sent from this tab again; paused writes leave memory, rows stay. */
  stop(): void;
  /** Where a final refusal is handed: the failed-write log. */
  handOffTo(handOff: WriteHandOff): void;
  /** May the gate draw this account's screens: has the store been read? */
  readyFor(account: AccountRef | null): boolean;
  /** The store could not be read: the gate says so, and offers `retryRead`. */
  unreadable(): boolean;
  retryRead(): void;
  /** Told when `readyFor` may answer differently. */
  subscribe(listener: () => void): () => void;
}

/** The slice of the photo queue a stopped tab drives. */
export interface IdentityUploads {
  stop(): void;
}

export function wireIdentity(opts: {
  queryClient: QueryClient;
  kv: Pick<Kv, 'get' | 'set' | 'del'>;
  routes: TabRoutes;
  beforeRestore?: (stored: unknown) => Promise<void>;
  timers?: Timers;
  markerStorage?: MarkerStorage;
  /** The queued writes (MAP 225b): the app's own unless a test hands it some. */
  writes?: IdentityWrites;
  /** Where the queued writes hand a final refusal: the failed-write log. */
  handOff?: WriteHandOff;
  /** The photo queue: the app's own unless a test hands it one. */
  uploads?: IdentityUploads;
  /** Moves an earlier release's queued writes out of the saved blob (legacy-writes.ts). */
  moveLegacyWrites?: MoveLegacyWrites;
}) {
  const writes = opts.writes ?? NO_WRITES;
  const uploads = opts.uploads ?? NO_UPLOADS;
  if (opts.handOff) writes.handOffTo(opts.handOff);
  const tabs = new IdentityTabs({
    ...opts.routes,
    timers: opts.timers,
    // Another tab changed who is signed in: this one draws nothing more, never writes the
    // blob again, sends nothing more, and drains no photo. All synchronous, inside `receive`,
    // so before the other tab's cookie can change.
    onStop: () => {
      cacheAccount.stop();
      writes.stop();
      uploads.stop();
    },
    // Reset, not invalidate: an answer given before the other tab's call landed must not be
    // drawn while the new one is on its way.
    onRecheck: () =>
      void opts.queryClient.resetQueries({
        predicate: (q) => isSessionQueryKey(q.queryKey),
      }),
  });
  const cacheAccount = new CacheAccount({
    queryClient: opts.queryClient,
    kv: opts.kv,
    dehydrateOptions,
    beforeRestore: opts.beforeRestore,
    onAccountChange: (account) => tabs.announce(accountKey(account)),
    markerStorage: opts.markerStorage,
    moveLegacyWrites: opts.moveLegacyWrites,
  });
  const persistOptions = {
    persister: cacheAccount.persister,
    maxAge: 1000 * 60 * 60 * 24,
    dehydrateOptions,
  } satisfies Omit<PersistQueryClientOptions, 'queryClient'>;
  return {
    cacheAccount,
    tabs,
    writes,
    persistOptions,
    persistNow: () => cacheAccount.persist(),
    listenToOtherTabs: () => tabs.listen(),
    beginTransition: () => tabs.beginTransition(),
    announceSignedOut: () => tabs.announce(null),
  };
}

export type Identity = ReturnType<typeof wireIdentity>;

/** A test that wires an identity without writes or photos gets ones that ignore it. */
const NO_WRITES: IdentityWrites = {
  setAccount: () => {},
  stop: () => {},
  handOffTo: () => {},
  readyFor: () => true,
  unreadable: () => false,
  retryRead: () => {},
  subscribe: () => () => {},
};
const NO_UPLOADS: IdentityUploads = { stop: () => {} };

export const identity = wireIdentity({
  queryClient,
  kv: idbKv,
  routes: browserRoutes(),
  beforeRestore: migrateLegacyDraftsOnThisDevice,
  markerStorage: safeLocalStorage(),
  writes: pendingWrites,
  handOff: failedWriteLog,
  uploads: uploadQueue,
  moveLegacyWrites: (stored) =>
    moveLegacyWrites(stored, {
      kv: idbKv,
      pending: indexedDbPendingWriteStore,
      failed: indexedDbFailedWriteStore,
      locks: browserLocks(),
    }),
});

/**
 * The saved cache's persister, and the options the provider saves with. The gate's own
 * writes go through the same persister: ⚠️ never wrap it (see `CacheAccount.persister`).
 */
export const { persistOptions, persistNow } = identity;
export const persister = persistOptions.persister;

/**
 * Call BEFORE sign-in, sign-up, joining a shop, creating or switching a shop, and sign-out:
 * every other tab stops drawing the old account before the cookie can change under it.
 */
export function beginIdentityTransition(): void {
  identity.beginTransition();
}

export function announceSignedOut(): void {
  identity.announceSignedOut();
}

/**
 * What the gate does with every network answer: check it against the saved cache's account,
 * unless this tab is stopped (it claims nothing) or held (the answer may predate another
 * tab's sign-in; it is asked again when the hold ends).
 */
export function confirmAnsweredSession(
  wired: Pick<Identity, 'cacheAccount'>,
  answer: SessionAnswer | undefined,
  tabs: TabsSnapshot,
): Promise<void> {
  if (answer === undefined || tabs.stopped || tabs.held) return Promise.resolve();
  return wired.cacheAccount.confirm(accountFromSession(answer));
}

/**
 * The identity RootGate uses: the app's own, unless a test hands it one it wired itself, so
 * no test has to reach into the app's singletons.
 */
const IdentityContext = createContext<Identity>(identity);
export const IdentityProvider = IdentityContext.Provider;
export function useIdentity(): Identity {
  return useContext(IdentityContext);
}

export function useCacheAccount(wired: Identity): CacheAccountSnapshot {
  const { cacheAccount } = wired;
  return useSyncExternalStore(
    cacheAccount.subscribe,
    cacheAccount.snapshot,
    cacheAccount.snapshot,
  );
}

export function useOtherTabs(wired: Identity): TabsSnapshot {
  return useTabsSnapshot(wired.tabs);
}
